import React from 'react';
import { isString } from '../../../util';

function getIndexForScreenId(screenids, screen, notFoundValue = -1) {
	if(isString(screen)) {
		const index = screenids.filter(s => s.id === screen);
		if(index.length > 0)return index[0].index;
	}
	return notFoundValue;
}

function getScreenByIndex(screenids, index) {
	const foundScreens = screenids.filter(({index:i}) => i === index);
	return foundScreens.length ? foundScreens[0] : null;
}

const ScreenSequence = React.memo(({children, goToScreenId, ...props}) => {

	//PROPS///////////////////////////////////

	const { 
		onComplete, onData, onRequestScreen, onScreenDisplay, 
		identifyScreens, // Function used to generate index <> id markers for navigation
		previousScreenResults:incomingPreviousScreenResults = [], 
		...restProps 
	} = props;
	
	//SCREEN DATA/////////////////////////////

	const screens = React.Children.toArray(children);
	const [previousScreenResults, setPreviousScreenResults] =  React.useState([]);

	const [screensById] = React.useState(identifyScreens ? identifyScreens(screens) : []);
	const [screenIndex, setScreenIndex] =  React.useState(getIndexForScreenId(screensById, goToScreenId, 0));
	const currentScreenById = getScreenByIndex(screensById, screenIndex);


	//NAVIGATION/////////////////////////////

	const back = (steps = 1) => {
		const count = screenIndex - steps;
		if(count >= 0) {
			setScreenIndex(count);
		} else {
			if(onRequestScreen)onRequestScreen(`back:${count*-1}`);
		}
	}

	const next = (steps = 1) => {
		let count = screenIndex + steps;
		if(count < screens.length) {
			setScreenIndex(count);
		} else {
			if(onRequestScreen) {
				count = steps - (screens.length -1 - screenIndex) ;
				onRequestScreen(`next:${count}`);
			}
		}
	}

	//SCEEEN CALLBACKS/////////////////////////////

	const handleOnSequenceComplete = (compiledResults) => {
		onComplete && onComplete(compiledResults);
		setPreviousScreenResults([]);
		screens.length > 1 && setScreenIndex(0);
	};
	const handleOnComplete = (formData, navigateTo) => {
		const compiledResults = [...previousScreenResults, ...(Array.isArray(formData) ? formData : [formData || {}])];
		if(onData && formData) onData(compiledResults);
		setPreviousScreenResults(compiledResults);
		
		if(isString(navigateTo)) {
			const goScreen = getIndexForScreenId(screensById, navigateTo);
			goScreen >= 0 ? setScreenIndex(goScreen) : onComplete && onComplete(compiledResults, navigateTo);
		} else {
			let nextScreenIndex = screenIndex+1;
			nextScreenIndex >= screens.length ? handleOnSequenceComplete(compiledResults) : setScreenIndex(nextScreenIndex);
		}
	};
	const handleOnRequestScreen = screen => {
		if(!screen) return;
		if(screen.match('^back')) {
			const [,count] = screen.match('^back:(\\d+)') || [];
			back(count && parseInt(count));
		} else if(screen.match('^next')) {
			const [,count] = screen.match('^next:(\\d+)') || [];
			next(count && parseInt(count));
		} else {
			const goScreen = getIndexForScreenId(screensById, screen);
			goScreen >=0 ? setScreenIndex(goScreen) : onRequestScreen(screen);
		}
	}

	//INIT/////////////////////////////

	/*
	This effect informs a parent sequence that it has rendered a screen with an id. 
	This is used for populating browser history.

	Only the sequence that owns the screen should report it. If a screen is designated 
	as a child screen, it will be ignored.
	*/
	React.useEffect(() => {
		if(onScreenDisplay && currentScreenById && !currentScreenById.isChildScreen) onScreenDisplay(currentScreenById);
	}, [currentScreenById, onScreenDisplay]);


	/*
	This effect will cause the sequence to navigate to the right screen 
	if the goToScreenId prop is passed with an id that this sequence knows about.

	It is important to note that sub-sequences and their ids can cause navigation 
	as the sequence move to the sub-sequence that contains it.
	*/
	React.useEffect(() => {
		if(goToScreenId) {
			const goToIndex = getIndexForScreenId(screensById, goToScreenId);
			if(goToIndex >= 0)  setScreenIndex(goToIndex);
		}
	}, [goToScreenId, screensById]); //eslint-disable-line react-hooks/exhaustive-deps
	


	return React.cloneElement(
			screens[screenIndex], 
			{ 
				onComplete:handleOnComplete, 
				onRequestScreen:handleOnRequestScreen, 
				onScreenDisplay,
				goToScreenId,
				previousScreenResults:[...incomingPreviousScreenResults, ...previousScreenResults], 
				...restProps 
			}
	)
		
});

export default ScreenSequence;